import { throttle } from 'throttle-debounce';
import { vesselIconSpeedBoat } from '../../../components/shared/vector-images';
import { telemetry } from '../../backend';
import { definitions, subscribe } from '../../message-broker';
import { transitionToRawURL } from '../../route/route-navigation';
import { getStore } from '../../store';
import { selectCurrentMemberId } from '../../store/modules/backend-service';
import { addSignals, api, getSelectedSignal } from '../../store/modules/signal';
import {
  layerExists,
  setDataSource,
  sourceExists,
  toggleLayerIfRequired,
} from '../_helpers';

let mapReference;
let lastT = 0;
let pulseCount = 0;

const pulsingDot = async (
  offsetX = 0,
  offsetY = 0,
  pulseSize = 200,
  subscriber = false,
) => ({
  width: pulseSize,
  height: pulseSize,
  data: new Uint8Array(pulseSize * pulseSize * 4),

  // When the layer is added to the map,
  // get the rendering context for the map canvas.
  onAdd() {
    const canvas = document.createElement('canvas');
    canvas.width = this.width;
    canvas.height = this.height;
    this.context = canvas.getContext('2d');
  },

  // Call once before every frame where the icon will be used.
  render() {
    const { context } = this;
    context.clearRect(0, 0, this.width, this.height);
    context.beginPath();

    const duration = 1000;
    const t = (performance.now() % duration) / duration;

    // Only draw
    if (t < lastT) {
      pulseCount += 1;
    }
    lastT = t;

    if (pulseCount % 3 === 0) {
      // Only show the pulse every 5 times
      const radius = (pulseSize / 2) * 0.15;
      const outerRadius = (pulseSize / 2) * 0.7 * t + radius;

      // Draw the outer circle.

      const rgbNumbers = subscriber ? '199, 133, 41' : '255, 92, 57';

      context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
      context.fillStyle = `rgba(${rgbNumbers}, ${1 - t})`;
      context.fill();

      // Draw the inner circle.
      context.beginPath();

      context.fillStyle = `rgba(${rgbNumbers}, 0.7)`;
      context.strokeStyle = 'white';
      context.lineWidth = 2 + 4 * (1 - t);
      context.fill();
      context.stroke();
    }

    // Update this image's data with data from the canvas.
    // The offsets are here to place the pulse behind the icon
    this.data = context.getImageData(
      offsetX,
      offsetY,
      this.width,
      this.height,
    ).data;

    // Continuously repaint the map, resulting
    // in the smooth animation of the dot.
    mapReference.triggerRepaint();

    // Return `true` to let the map know that the image was updated.
    return true;
  },
});

const layerName = 'signalPositions';
const profilePulseLayerName = 'signal-profile-pulse';
const subscriberProfilePulseLayerName = 'signal-profile-subscribed-pulse';
const anonymousPulseLayerName = 'signal-anonymous-pulse';
const subscriberAnonymousPulseLayerName = 'signal-anonymous-subscribed-pulse';

const subscriberPulsingDot = async (
  offsetX = 0,
  offsetY = 0,
  pulseSize = 200,
) => pulsingDot(offsetX, offsetY, pulseSize, true);

const loadMarkerImages = async (imageIds) => {
  // Add the marker images from the endpoint to the map if unique and not already loaded
  if (!mapReference.hasImage(profilePulseLayerName)) {
    const pulse = await pulsingDot();
    mapReference.addImage(profilePulseLayerName, pulse);
  }
  if (!mapReference.hasImage(subscriberProfilePulseLayerName)) {
    const pulse = await subscriberPulsingDot();
    mapReference.addImage(subscriberProfilePulseLayerName, pulse);
  }
  if (!mapReference.hasImage(subscriberAnonymousPulseLayerName)) {
    const pulse = await subscriberPulsingDot(0, 25);
    mapReference.addImage(subscriberAnonymousPulseLayerName, pulse);
  }
  // Keep track of IDs that failed to load, to use default icon as fallback
  const failedImagesToLoad = [];
  const promiseArray = imageIds.map(async (imageId) => {
    if (imageId && !mapReference.hasImage(imageId)) {
      try {
        await new Promise((resolve, reject) => {
          mapReference.loadImage(
            `/public/app-main/image/pinWrapImage/${imageId}`,
            (error, image) => {
              if (error) {
                failedImagesToLoad.push(imageId);
                return reject(error);
              }
              mapReference.addImage(imageId, image);
              return resolve();
            },
          );
        });
      } catch (error) {
        // We handle the error in the catch block, but we don't want to stop the loop
        // console.error(`Failed to load image ${imageId}:`, error);
      }
    }
    return true;
  });
  await Promise.all(promiseArray);
  return failedImagesToLoad;
};
// eslint-disable-next-line no-unused-vars
let selectedMarkerId;
let requestDataTimeout;
let currentViewPort;
let lastViewPortBounds;

let getPositionsAbortController;
const getPositions = async () => {
  try {
    // If there isn't a view port to query for bounds, don't do anything.
    if (!lastViewPortBounds) return [];

    if (getPositionsAbortController) {
      getPositionsAbortController.abort();
    }

    const controller = new AbortController();
    const { signal } = controller;
    getPositionsAbortController = controller;

    const { data } = await api.getPositions(
      {
        getPositionsRequest: {
          boundingBox: lastViewPortBounds,
        },
      },
      {
        signal,
      },
    );

    const memberId = selectCurrentMemberId(getStore().getState());

    return data?.filter((r) => r.memberid !== memberId) || [];
  } catch (error) {
    telemetry(
      telemetry.WARN,
      `[signalMarkers mapLayer] Failed to load public positions, ${error}`,
    );
    return [];
  }
};

const defaultIcon = 'vesselIconSpeedBoat';

const signalDataCache = {};

const cacheTTL = 1 * 60 * 1000;

// We want to clear out the cache on an interval to ensure that old boats don't stay on the screen
setInterval(() => {
  Object.getOwnPropertyNames(signalDataCache).forEach((property) => {
    delete signalDataCache[property];
  });
}, cacheTTL);

export const fetchAndBuildDataSource = async () => {
  // This is to remove any selected markers
  const data = await getPositions();
  if (data.length > 0) {
    data.forEach((item) => {
      signalDataCache[item.id] = item;
    });
  }
  const dataFromCache = Object.entries(signalDataCache).map(
    ([_, value]) => value,
  );

  getStore().dispatch(addSignals(data));

  const uniqueMarkerImageIds = [
    ...new Set(dataFromCache.map((position) => position.image)),
  ];

  const failedImagesToLoad = await loadMarkerImages(uniqueMarkerImageIds);

  const geojson = {
    type: 'FeatureCollection',
    features: dataFromCache.map((item) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [item.longitude, item.latitude],
      },
      properties: {
        ...item,
        image: failedImagesToLoad.includes(item.image)
          ? defaultIcon
          : item.image || defaultIcon,

        markerSelected: selectedMarkerId === item.id,
      },
    })),
  };
  setDataSource(mapReference, layerName, geojson);

  const profileGeoJSON = {
    type: 'FeatureCollection',
    features: dataFromCache
      .filter((item) => !failedImagesToLoad.includes(item.image))
      .filter((item) => item.image)
      .filter((item) => !item.subscribed)
      .map((item) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [item.longitude, item.latitude],
        },
        properties: {
          ...item,
          image: item.image,

          markerSelected: selectedMarkerId === item.id,
        },
      })),
  };
  setDataSource(mapReference, profilePulseLayerName, profileGeoJSON);

  const profileSubscribedGeoJSON = {
    type: 'FeatureCollection',
    features: dataFromCache
      .filter((item) => !failedImagesToLoad.includes(item.image))
      .filter((item) => item.image)
      .filter((item) => item.subscribed)
      .map((item) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [item.longitude, item.latitude],
        },
        properties: {
          ...item,
          image: item.image,

          markerSelected: selectedMarkerId === item.id,
        },
      })),
  };
  setDataSource(
    mapReference,
    subscriberProfilePulseLayerName,
    profileSubscribedGeoJSON,
  );

  const anonymousGeoJSON = {
    type: 'FeatureCollection',
    features: dataFromCache
      .filter((item) => failedImagesToLoad.includes(item.image) || !item.image)
      .filter((item) => !item.subscribed)
      .map((item) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [item.longitude, item.latitude],
        },
        properties: {
          ...item,
          image: defaultIcon,

          markerSelected: selectedMarkerId === item.id,
        },
      })),
  };
  setDataSource(mapReference, anonymousPulseLayerName, anonymousGeoJSON);

  const anonymousSubscribedGeoJSON = {
    type: 'FeatureCollection',
    features: dataFromCache
      .filter((item) => failedImagesToLoad.includes(item.image) || !item.image)
      .filter((item) => item.subscribed)
      .map((item) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [item.longitude, item.latitude],
        },
        properties: {
          ...item,
          image: defaultIcon,

          markerSelected: selectedMarkerId === item.id,
        },
      })),
  };
  setDataSource(
    mapReference,
    subscriberAnonymousPulseLayerName,
    anonymousSubscribedGeoJSON,
  );
};
export const throttledFetchAndBuildDataSource = throttle(
  1000,
  fetchAndBuildDataSource,
);
const addLayersIfRequired = async () => {
  if (!mapReference) return;

  if (!mapReference.hasImage(anonymousPulseLayerName)) {
    const pulsingDefaultIcon = await pulsingDot(0, 25);
    mapReference.addImage(anonymousPulseLayerName, pulsingDefaultIcon);
  }

  if (!sourceExists(mapReference, layerName)) {
    mapReference.addSource(layerName, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    mapReference.addSource(profilePulseLayerName, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    mapReference.addSource(subscriberProfilePulseLayerName, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    mapReference.addSource(anonymousPulseLayerName, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    mapReference.addSource(subscriberAnonymousPulseLayerName, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }

  if (!layerExists(mapReference, layerName)) {
    mapReference.addLayer({
      id: profilePulseLayerName,
      type: 'symbol',
      source: profilePulseLayerName,
      layout: {
        'icon-image': profilePulseLayerName,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          3,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
    mapReference.addLayer({
      id: subscriberProfilePulseLayerName,
      type: 'symbol',
      source: subscriberProfilePulseLayerName,
      layout: {
        'icon-image': subscriberProfilePulseLayerName,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          3,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
    mapReference.addLayer({
      id: anonymousPulseLayerName,
      type: 'symbol',
      source: anonymousPulseLayerName,
      layout: {
        'icon-image': anonymousPulseLayerName,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          3,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
    mapReference.addLayer({
      id: subscriberAnonymousPulseLayerName,
      type: 'symbol',
      source: subscriberAnonymousPulseLayerName,
      layout: {
        'icon-image': subscriberAnonymousPulseLayerName,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          3,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
    mapReference.addLayer({
      id: layerName,
      type: 'symbol',
      source: layerName,
      layout: {
        'icon-image': ['to-string', ['get', 'image']],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          3,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }
};

const layer = {
  displayName: 'Public positions',
  synonyms: '',
  group: '',
  typeUrl: '',
  icon: {
    baseShape: vesselIconSpeedBoat,
    iconColor: '#fff',
    markerColor: '#139ECA',
  },
  shownByDefault: true,
  relativeDepth: 0,
  menuOptions: {
    visible: false,
    owningMenu: 'marine-points-of-interest',
    order: 5,
  },
};

const updateData = async (force) => {
  // If we have yet to receive a reference to the map object through the onInitialize function,
  // just return as we can't update the data on the layer if the layer doesn't exist yet.
  if (!mapReference) return;

  addLayersIfRequired();

  const state = getStore().getState();

  const { hidingAllLayers } = state.appControls;

  if (hidingAllLayers) {
    toggleLayerIfRequired(mapReference, layerName, false);
    return;
  }

  toggleLayerIfRequired(mapReference, layerName, true);

  let bounds = state.appControls?.currentMapParams?.viewPortDetails?.bounds;

  // If there isn't a view port to query for bounds, don't do anything.
  if (!bounds) {
    if (!currentViewPort) {
      return;
    }

    bounds = currentViewPort.bounds;
  }

  // If we're not forcing the update, and the bounds are the same as the
  // previous call, don't do anything
  if (!force && bounds === lastViewPortBounds) return;
  lastViewPortBounds = bounds;

  // Update the data for this layer every minute when the viewport doesn't change.
  if (requestDataTimeout) clearTimeout(requestDataTimeout);
  requestDataTimeout = setTimeout(() => updateData(true), 60 * 1000);

  if (force) {
    await fetchAndBuildDataSource();
  } else {
    await throttledFetchAndBuildDataSource();
  }
};
layer.onInitialize = (map) => {
  // eslint-disable-next-line no-multi-assign
  mapReference = map;
  addLayersIfRequired();

  map.on('deckee-press', layerName, async (event) => {
    // This is to select a marker and go to the popover
    const { id } = event.features[0].properties;

    selectedMarkerId = id;
    transitionToRawURL(`/maps/signal/${id}`);
    event.originalEvent.preventDefault();
  });

  getStore().subscribe(() => {
    const state = getStore().getState();
    const signal = getSelectedSignal(state);
    const selectedId = signal && signal.id;
    if (selectedId !== selectedMarkerId) {
      selectedMarkerId = selectedId;
      updateData(true);
    }
  });
};

layer.onToggle = (map, show) => {
  toggleLayerIfRequired(map, layerName, show);
  toggleLayerIfRequired(map, profilePulseLayerName, show);
  toggleLayerIfRequired(map, subscriberProfilePulseLayerName, show);
  toggleLayerIfRequired(map, anonymousPulseLayerName, show);
  toggleLayerIfRequired(map, subscriberAnonymousPulseLayerName, show);
};

subscribe(definitions.map.viewPortUpdated, (viewPort) => {
  currentViewPort = viewPort;

  updateData();
});

export default layer;
